import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, RefreshCw } from "lucide-react";
import { DeltaDirection } from "@boo/shared";
import type { MonthlySummaryPageData } from "../../types";
import { triggerBankImport } from "@/services/expenseApi";
import { getGmailConnectionStatus } from "@/services/gmailIntegrationApi";
import { useAppStore } from "../../store/useAppStore";
import { useFeatureFlagStore } from "../../../feature-flags/store/useFeatureFlagStore";
import { FeatureFlag } from "../../../feature-flags/types/featureFlags.types";
import { useRecurringExpenseStore } from "../../../recurring-expenses/store/useRecurringExpenseStore";

interface HeroSectionProps {
  data: MonthlySummaryPageData;
}

function formatBaht(n: number) {
  return n.toLocaleString("en-US");
}

type ImportStatus = "idle" | "loading" | "done" | "error";

export default function HeroSection({ data }: HeroSectionProps) {
  const isEmpty = data.totalSpend === 0 && data.income === 0;
  const hasRealDelta = data.deltaPercent > 0;
  const isUp = data.deltaDirection === DeltaDirection.Up;
  const [importStatus, setImportStatus] = useState<ImportStatus>("idle");
  const [importedCount, setImportedCount] = useState(0);
  const [gmailConnected, setGmailConnected] = useState(false);

  useEffect(() => {
    async function loadGmailStatus() {
      try {
        const { connected } = await getGmailConnectionStatus();
        setGmailConnected(connected);
      } catch {
        setGmailConnected(false);
      }
    }
    void loadGmailStatus();
  }, []);

  async function handleImport() {
    if (importStatus === "loading") return;
    setImportStatus("loading");
    try {
      const { imported } = await triggerBankImport();
      setImportedCount(imported);
      setImportStatus("done");
      // A merge into an existing row changes data without counting as imported.
      const { viewYear, viewMonth, loadMonth, loadPendingReconciliationCount } =
        useAppStore.getState();
      void loadMonth(viewYear, viewMonth);
      void loadPendingReconciliationCount();
      if (useFeatureFlagStore.getState().flags[FeatureFlag.Recurring]) {
        void useRecurringExpenseStore.getState().loadSummary();
      }
    } catch {
      setImportStatus("error");
    } finally {
      setTimeout(() => setImportStatus("idle"), 4000);
    }
  }

  return (
    <div className="rounded-[14px] px-5 py-5 sm:px-6 bg-card border-[0.5px] border-border shadow-card">
      <div>
        {/* Eyebrow */}
        <div className="flex items-center justify-between gap-2 mb-2.5">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
            <span className="text-[11px] tracking-[0.08em] text-[var(--text-secondary)]">
              total spent · {data.month}
            </span>
          </div>
          {gmailConnected && (
            <button
              onClick={handleImport}
              disabled={importStatus === "loading"}
              aria-label="Import bank"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
                importStatus === "loading"
                  ? "opacity-50 cursor-not-allowed text-muted-foreground"
                  : importStatus === "error"
                    ? "text-red-500 hover:bg-red-500/10"
                    : importStatus === "done"
                      ? "text-muted-foreground"
                      : "text-muted-foreground hover:bg-black/5 dark:hover:bg-white/10"
              }`}
            >
              <RefreshCw
                className={`w-3.5 h-3.5 flex-shrink-0 ${importStatus === "loading" ? "animate-spin" : ""}`}
              />
              {importStatus === "loading" && "Importing…"}
              {importStatus === "done" &&
                (importedCount > 0
                  ? `${importedCount} imported`
                  : "Up to date")}
              {importStatus === "error" && "Import failed"}
              {importStatus === "idle" && (
                <span className="hidden sm:inline">Import bank</span>
              )}
            </button>
          )}
        </div>

        {/* Big number */}
        <div className="font-bold leading-none flex items-baseline gap-1.5 mb-4 text-[clamp(48px,9vw,88px)] tracking-[-0.035em] text-foreground">
          <span className="text-[0.5em] font-medium text-muted-foreground mr-1">
            ฿
          </span>
          {formatBaht(data.totalSpend)}
          <span className="text-[0.42em] font-medium text-muted-foreground tracking-[-0.02em]">
            .{String(data.totalSpendCents).padStart(2, "0")}
          </span>
        </div>

        {/* Delta row */}
        <div className="flex items-center gap-x-3.5 gap-y-2 flex-wrap mb-5 sm:mb-6">
          {isEmpty ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium bg-[var(--surface-muted)] text-[var(--text-secondary)]">
              no transactions yet
            </span>
          ) : (
            <>
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium ${
                  !hasRealDelta
                    ? "bg-[var(--surface-muted)] text-[var(--text-secondary)]"
                    : isUp
                      ? "bg-[var(--color-danger-bg)] text-[var(--color-danger-fg)]"
                      : "bg-[var(--color-success-bg)] text-[var(--color-success-fg)]"
                }`}
              >
                {hasRealDelta &&
                  (isUp ? (
                    <TrendingUp className="w-3.5 h-3.5" />
                  ) : (
                    <TrendingDown className="w-3.5 h-3.5" />
                  ))}
                {data.deltaPercent}% vs{" "}
                {data.monthIndex === 2 ? "February" : "last month"}
              </span>
              <span className="text-[12px] text-[var(--text-secondary)]">
                <span className="hidden sm:inline">
                  <b className="text-foreground font-medium">
                    ฿{formatBaht(data.deltaAmount)}
                  </b>{" "}
                  more than last month ·{" "}
                </span>
                <b className="text-foreground font-medium">
                  ฿{formatBaht(data.dailyAverage)}
                </b>{" "}
                daily average
              </span>
            </>
          )}
        </div>

        {/* Mini stat tiles */}
        <div className="grid grid-cols-3 divide-x divide-border border-t border-border pt-4 sm:divide-x-0 sm:border-t-0 sm:pt-0 sm:gap-3">
          <div className="min-w-0 px-3 pl-0 sm:rounded-xl sm:px-4 sm:py-3.5 sm:bg-[var(--surface-muted)]">
            <div className="text-[11px] mb-0.5 text-muted-foreground">
              Income
            </div>
            {isEmpty ? (
              <div className="text-[15px] sm:text-[17px] font-bold tracking-tight [font-variant-numeric:tabular-nums] text-muted-foreground">
                —
              </div>
            ) : (
              <div className="text-[15px] sm:text-[17px] font-bold tracking-tight [font-variant-numeric:tabular-nums] text-[var(--color-income)]">
                ฿{formatBaht(data.income)}
              </div>
            )}
            <div
              className={`text-[10px] mt-0.5 tracking-[0.04em] text-muted-foreground ${isEmpty ? "" : "hidden sm:block"}`}
            >
              {isEmpty
                ? "no income recorded"
                : `฿${formatBaht(data.income)} this month`}
            </div>
          </div>
          <div className="min-w-0 px-3 sm:rounded-xl sm:px-4 sm:py-3.5 sm:bg-[var(--surface-muted)]">
            <div className="text-[11px] mb-0.5 text-muted-foreground">
              Saved
            </div>
            {isEmpty ? (
              <div className="text-[15px] sm:text-[17px] font-bold tracking-tight [font-variant-numeric:tabular-nums] text-muted-foreground">
                —
              </div>
            ) : (
              <div className="text-[15px] sm:text-[17px] font-bold tracking-tight [font-variant-numeric:tabular-nums] text-foreground">
                ฿{formatBaht(data.saved)}
              </div>
            )}
            <div className="text-[10px] mt-0.5 tracking-[0.04em] text-muted-foreground">
              {isEmpty ? "add income to track" : `${data.savingsRate}% rate`}
            </div>
          </div>
          <div className="min-w-0 px-3 pr-0 sm:rounded-xl sm:px-4 sm:py-3.5 sm:bg-[var(--surface-muted)]">
            <div className="text-[11px] mb-0.5 text-muted-foreground">
              Biggest day
            </div>
            {isEmpty ? (
              <div className="text-[15px] sm:text-[17px] font-bold tracking-tight [font-variant-numeric:tabular-nums] text-muted-foreground">
                —
              </div>
            ) : (
              <div className="text-[15px] sm:text-[17px] font-bold tracking-tight [font-variant-numeric:tabular-nums] text-[var(--color-expense)]">
                ฿{formatBaht(data.biggestDayAmount)}
              </div>
            )}
            <div className="text-[10px] mt-0.5 tracking-[0.04em] text-muted-foreground">
              {isEmpty ? "no spending yet" : data.biggestDayDate}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
